import type { ResonancePhase } from '../simulation/dropBag';
import { MAX_CHARACTER_STAGE } from '../content/progression';
import { DEFAULT_GAME_MODE, type GameModeId } from '../content/modes';

export type GameRecord = Readonly<{
  version: 1;
  highScore: number;
  highestStage: number;
  maxChain: number;
  highestPhase: ResonancePhase;
  observerReached: boolean;
}>;

export type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const STORAGE_KEY = 'ishi-no-kyomei.record.v1';

export const EMPTY_RECORD: GameRecord = {
  version: 1,
  highScore: 0,
  highestStage: 1,
  maxChain: 0,
  highestPhase: 1,
  observerReached: false,
};

function isSafeInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value);
}

function isRecord(value: unknown): value is GameRecord {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return (
    record.version === 1 &&
    isSafeInteger(record.highScore) &&
    isSafeInteger(record.highestStage) &&
    isSafeInteger(record.maxChain) &&
    isSafeInteger(record.highestPhase) &&
    typeof record.observerReached === 'boolean' &&
    (record.highScore as number) >= 0 &&
    (record.highestStage as number) >= 1 &&
    (record.highestStage as number) <= MAX_CHARACTER_STAGE &&
    (record.maxChain as number) >= 0 &&
    (record.highestPhase as number) >= 1 &&
    (record.highestPhase as number) <= 5
  );
}

export class RecordStore {
  private readonly key: string;
  public constructor(private readonly storage: StorageLike, mode: GameModeId = DEFAULT_GAME_MODE) {
    this.key = mode === DEFAULT_GAME_MODE ? STORAGE_KEY : `${STORAGE_KEY}.${mode}`;
  }

  public load(): GameRecord {
    try {
      const raw = this.storage.getItem(this.key);
      if (raw === null) return EMPTY_RECORD;
      const parsed: unknown = JSON.parse(raw);
      if (isRecord(parsed)) return parsed;
    } catch {
      // Invalid or inaccessible data is replaced below when storage permits it.
    }
    this.write(EMPTY_RECORD);
    return EMPTY_RECORD;
  }

  public save(candidate: Omit<GameRecord, 'version'>): GameRecord {
    const previous = this.load();
    const next: GameRecord = {
      version: 1,
      highScore: Math.max(previous.highScore, candidate.highScore),
      highestStage: Math.max(previous.highestStage, candidate.highestStage),
      maxChain: Math.max(previous.maxChain, candidate.maxChain),
      highestPhase: Math.max(previous.highestPhase, candidate.highestPhase) as ResonancePhase,
      observerReached: previous.observerReached || candidate.observerReached,
    };
    this.write(next);
    return next;
  }

  public clear(): GameRecord {
    try {
      this.storage.removeItem(this.key);
    } catch {
      // Preserve reset semantics when removal alone is restricted.
      this.write(EMPTY_RECORD);
    }
    return EMPTY_RECORD;
  }

  private write(record: GameRecord): void {
    try {
      this.storage.setItem(this.key, JSON.stringify(record));
    } catch {
      // Storage can be unavailable in private or quota-constrained contexts.
    }
  }
}
